import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const INPUT = 'w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20';

const eventName = event => event?.name || event?.title || event?.event_id || event?.id || '';
const eventId = event => event?.event_id || event?.id || '';

function eventList(payload) {
    if (Array.isArray(payload)) return payload;
    return Array.isArray(payload?.data) ? payload.data : [];
}

/**
 * Searchable event selector for campaign event filters.
 *
 * The persisted value remains the event id, while the visible input uses the event name. Exact ids
 * are still accepted for compatibility with saved drafts and API-driven callers that have no list match.
 */
export default function EventSelect({ value, onChange, label = 'Specific event (optional)' }) {
    const { getAuthHeader } = useAuth() || {};
    const [events, setEvents] = useState([]);
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const selected = useMemo(() => events.find(event => eventId(event) === value), [events, value]);
    const filteredEvents = useMemo(() => {
        const normalized = query.trim().toLowerCase();
        if (!normalized || selected && query === eventName(selected)) return events;
        return events.filter(event => `${eventName(event)} ${eventId(event)}`.toLowerCase().includes(normalized));
    }, [events, query, selected]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        axios.get(`${API}/events`, {
            params: { page: 1, page_size: 100 },
            headers: getAuthHeader?.() || {},
        })
            .then(res => {
                if (!cancelled) setEvents(eventList(res.data));
            })
            .catch(() => {
                if (!cancelled) setEvents([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [getAuthHeader]);

    useEffect(() => {
        if (!value) {
            setQuery('');
            return undefined;
        }

        const known = events.find(event => eventId(event) === value);
        if (known) {
            setQuery(eventName(known));
            return undefined;
        }

        let cancelled = false;
        axios.get(`${API}/events/${encodeURIComponent(value)}`, { headers: getAuthHeader?.() || {} })
            .then(res => {
                if (!cancelled) {
                    const event = res.data;
                    if (event) {
                        setEvents(previous => previous.some(item => eventId(item) === eventId(event))
                            ? previous
                            : [...previous, event]);
                        setQuery(eventName(event) || value);
                    } else {
                        setQuery(value);
                    }
                }
            })
            .catch(() => {
                if (!cancelled) setQuery(value);
            });
        return () => { cancelled = true; };
    }, [value, events, getAuthHeader]);

    const handleInput = (nextQuery) => {
        setQuery(nextQuery);
        setOpen(true);
        const match = events.find(event => eventName(event).toLowerCase() === nextQuery.trim().toLowerCase()
            || eventId(event) === nextQuery.trim());
        onChange(match ? eventId(match) : (nextQuery.trim() || undefined));
    };

    const selectEvent = event => {
        const id = eventId(event);
        setQuery(eventName(event));
        setOpen(false);
        onChange(id || undefined);
    };

    return (
        <div className="relative space-y-1">
            <label className="block text-xs font-medium text-stone-600">{label}</label>
            <input
                value={query}
                onChange={e => handleInput(e.target.value)}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
                placeholder="Search events by name or paste event id"
                className={INPUT}
                aria-label={label}
                aria-autocomplete="list"
                aria-expanded={open}
            />
            {open && (loading || filteredEvents.length > 0) && (
                <div className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto rounded-lg border border-stone-200 bg-white shadow-lg" role="listbox">
                    {loading && <p className="px-3 py-2 text-xs text-stone-400">Loading events...</p>}
                    {!loading && filteredEvents.map(event => {
                        const id = eventId(event);
                        return (
                            <button
                                key={id}
                                type="button"
                                role="option"
                                aria-selected={id === value}
                                onMouseDown={e => e.preventDefault()}
                                onClick={() => selectEvent(event)}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-stone-50"
                            >
                                <span className="block truncate text-stone-800">{eventName(event)}</span>
                                <span className="block truncate text-xs text-stone-400">{id}</span>
                            </button>
                        );
                    })}
                </div>
            )}
            {selected && <p className="text-xs text-stone-500">Selected: {eventName(selected)}</p>}
        </div>
    );
}
